import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSession } from "../session";

interface FavoritesApi {
  has(gid: number): boolean | undefined;
  ensure(gids: number[]): void;
  add(gid: number): Promise<void>;
  remove(gid: number): Promise<void>;
  changed(gid?: number): void;
  revision: number;
  /** a bookmark was made, edited, deleted, saved or unsaved: lists of bookmarks are stale */
  bookmarksChanged(): void;
  bookmarksRevision: number;
}
const Ctx = createContext<FavoritesApi | null>(null);
export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { client } = useSession();
  const [known, setKnown] = useState<ReadonlyMap<number, boolean>>(new Map());
  const [revision, setRevision] = useState(0);
  const [bookmarksRevision, setBookmarksRevision] = useState(0);
  const bookmarksChanged = useCallback(() => setBookmarksRevision((v) => v + 1), []);
  const values = useRef(new Map<number, boolean>()),
    versions = useRef(new Map<number, number>()),
    pending = useRef(new Set<number>()),
    queued = useRef(new Set<number>()),
    scheduled = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const ensure = useCallback(
    (gids: number[]) => {
      for (const gid of gids)
        if (!values.current.has(gid) && !pending.current.has(gid)) queued.current.add(gid);
      if (scheduled.current) return;
      scheduled.current = true;
      queueMicrotask(() => {
        scheduled.current = false;
        const ids = [...queued.current];
        queued.current.clear();
        for (let i = 0; i < ids.length; i += 100) {
          const batch = ids.slice(i, i + 100),
            snapshot = new Map(batch.map((gid) => [gid, versions.current.get(gid) ?? 0]));
          for (const gid of batch) pending.current.add(gid);
          client
            .call("personal.bookmarkMemberships", { body: { gids: batch } })
            .then(
              (result) => {
                if (!alive.current) return;
                for (const item of result.items)
                  if (snapshot.get(item.gid) === (versions.current.get(item.gid) ?? 0))
                    values.current.set(item.gid, item.isFavorite);
                setKnown(new Map(values.current));
              },
              () => {},
            )
            .finally(() => {
              for (const gid of batch) pending.current.delete(gid);
            });
        }
      });
    },
    [client],
  );
  const changed = useCallback(
    (gid?: number) => {
      if (gid !== undefined) {
        versions.current.set(gid, (versions.current.get(gid) ?? 0) + 1);
        values.current.delete(gid);
        pending.current.delete(gid);
        setKnown(new Map(values.current));
        ensure([gid]);
      }
      setRevision((v) => v + 1);
    },
    [ensure],
  );
  const change = useCallback(
    async (gid: number, add: boolean) => {
      const version = (versions.current.get(gid) ?? 0) + 1;
      versions.current.set(gid, version);
      await client.call(add ? "personal.addFavorite" : "personal.removeFavorite", { params: { gid } });
      if (!alive.current || versions.current.get(gid) !== version) return;
      values.current.set(gid, add);
      setKnown(new Map(values.current));
      setRevision((v) => v + 1);
    },
    [client],
  );
  const api = useMemo<FavoritesApi>(
    () => ({
      has: (gid) => known.get(gid),
      ensure,
      add: (gid) => change(gid, true),
      remove: (gid) => change(gid, false),
      changed,
      revision,
      bookmarksChanged,
      bookmarksRevision,
    }),
    [known, ensure, change, changed, revision, bookmarksChanged, bookmarksRevision],
  );
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
export function useFavorites() {
  const value = useContext(Ctx);
  if (!value) throw new Error("useFavorites outside FavoritesProvider");
  return value;
}
